import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBed, FiHome, FiWifi, FiCoffee, FiCar, FiUsers } = FiIcons;

const Accommodation = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const rooms = [
    {
      name: 'Master Suite',
      description: 'Wake up to breathtaking mountain views from your bed',
      features: ['King-size bed', 'Private balcony', 'Mountain view', 'En-suite bathroom'],
      image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&h=400&fit=crop'
    },
    {
      name: 'Garden Rooms',
      description: 'Direct access to our manicured tea estate grounds',
      features: ['Twin beds', 'Garden access', 'Shared bathroom', 'Tea estate views'],
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop'
    }
  ];

  const amenities = [
    { icon: FiHome, title: 'Entire Bungalow', desc: 'Complete privacy for your group' },
    { icon: FiWifi, title: 'High-Speed WiFi', desc: 'Perfect for digital nomads' },
    { icon: FiCoffee, title: 'Kitchen Facilities', desc: 'Fully equipped for self-catering' },
    { icon: FiCar, title: 'Parking & Driver Quarters', desc: 'Secure parking with driver accommodation' },
    { icon: FiUsers, title: 'Up to 8 Guests', desc: 'Ideal for families and groups' },
    { icon: FiBed, title: 'Premium Linens', desc: 'High thread count for ultimate comfort' },
  ];

  return (
    <section id="accommodation" className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-emerald-900/20 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white font-serif mb-6">
            Luxury Accommodation
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Experience the perfect blend of colonial heritage and modern comfort in our 
            thoughtfully appointed bungalow rooms.
          </p>
        </motion.div>

        {/* Room Showcase */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {rooms.map((room, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 + index * 0.2 }}
              className="glass-card rounded-2xl overflow-hidden"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={room.image}
                  alt={room.name}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-3 font-serif">{room.name}</h3>
                <p className="text-gray-300 mb-6">{room.description}</p>
                
                <div className="space-y-2">
                  {room.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center text-gray-300">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full mr-3"></div>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Amenities Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h3 className="text-3xl font-bold text-white text-center mb-12 font-serif">
            Premium Amenities
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {amenities.map((amenity, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-card p-6 rounded-xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={amenity.icon} className="w-8 h-8 text-emerald-300" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">{amenity.title}</h4>
                <p className="text-gray-300 text-sm">{amenity.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Floor Plan Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20"
        >
          <div className="glass-card p-8 rounded-2xl text-center">
            <h3 className="text-3xl font-bold text-white mb-6 font-serif">
              Bungalow Layout
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Our colonial-style bungalow features spacious rooms arranged around a central 
              living area, with wide verandas offering panoramic views of the surrounding 
              tea estate and mountains.
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="glass-button px-8 py-3 rounded-full font-semibold"
            >
              View Floor Plan
            </motion.button>
          </div>
        </motion.div>

        {/* Booking CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-16 text-center"
        >
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-3xl font-bold text-white mb-4 font-serif">
              Ready for Your Mountain Retreat?
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Book your stay at Akkara Dahaya Estate and experience the perfect blend 
              of luxury, nature, and tranquility.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="glass-button px-8 py-4 rounded-full text-lg font-semibold pulse-glow"
              >
                Check Availability
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="glass-morphism px-8 py-4 rounded-full text-lg font-semibold border-2 border-white/30 hover:border-emerald-300/50 transition-colors text-white"
              >
                Request Information
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Accommodation;